/* Flow-grouped row sets for the summary cards and the per-device cards.
 *
 * ── What changed and why ──────────────────────────────────────────────────
 * A card used to be a flat list of unrelated metrics: Storage showed Discharge
 * Max (kW), Charge Max (kW), Discharge Total (kWh), Charge Total (kWh), with
 * the money somewhere else entirely. Reading "what did charging cost me?" meant
 * pairing a number in one corner with a number in another.
 *
 * Cards are now grouped BY FLOW. One row per flow, and that row carries the
 * flow's power, its energy and its money:
 *
 *     Charged      <kW>   <kWh>   <$>
 *     Discharge    <kW>   <kWh>   <$>
 *     Driving    † <kW>   <kWh>   <$>     <- EV only, memo line
 *     Losses     † <kW>   <kWh>   <$>     <- memo line
 *     Demand                      <$>     <- its own line, never folded in
 *     NET TOTAL                   <$>     <- energy + demand
 *
 * A storage device's energy has exactly those exits, and the four rows close
 * against the engine's own identity:
 *
 *     Charged - Discharged - Driving - Losses - dStored = 0
 *
 * verified per device on husky-2040-ref. Before the Losses row the largest
 * outflow on the card was the one nobody controlled and nobody could see: one
 * EV loses 93,007.9 kWh a year -- exactly 5% of what it is charged -- and the
 * card reported it nowhere.
 *
 * ── The total, and the rows that add up to it ─────────────────────────────
 * The total is the card's NET: the engine's category cost -- which is the
 * ENERGY value -- plus that card's demand charge. The demand row keeps its own
 * line and is added into the net rather than replaced by it.
 *
 * It is NOT computed by adding up the rows on screen. It is two NAMED figures,
 * and only ever two: see cardNetTotal below for the arithmetic, the sign
 * convention that differs between them, and what happens to the unattributed
 * remainder of a ratcheted charge. No row can move a total.
 *
 * The rows are nonetheless BUILT to add up to it: on every card, the rows
 * without a dagger sum to the total. Two rows exist to make that true, and
 * each is a difference between engine totals rather than a price worked out
 * here:
 *
 *     Fuel  = Production Generation Value - Production Cost
 *     Held  = Storage Cost - Storage Charge Cost - Storage Discharge Value
 *
 * A generator and a battery are the same kind of device. Each buys an input --
 * gas, or off-peak electricity -- and turns it into an output worth more, so
 * the two cards read the same way, input cost first: Production is Fuel then
 * Generated, Storage is Charged, Discharge, then Held -- energy the battery
 * bought and has not used yet. See storageHeldExact for what Held measures.
 *
 * ── Transfer is the utility bill; one line connects it to the rest ────────
 * Transfer carries only what crossed the utility meter -- Import, Export,
 * Demand -- and its total, labelled "Utility bill", is their sum. Fuel is not
 * on it. Fuel is on Production, once, netted into that card's total, so no
 * dollar appears in two totals. What joins the cards is one line under
 * Transfer:
 *
 *     Site net cost = utility bill + generator fuel + held in storage
 *
 * The site net is the engine's `Transfer Cost ($)` -- Production + Consumption
 * + Storage -- plus the billed demand. It closes to within the engine's
 * per-hour rounding; see buildSiteReconciliation.
 *
 * Driving and Losses are both excluded, for different reasons, and both are
 * already inside `Storage Cost ($)`:
 *
 *   Driving is energy already paid for when it was charged, and the engine
 *   expenses it there directly. Counting it again overstates the cost.
 *
 *   The loss COST is a reclassification of money already in the total -- the
 *   charging loss capitalised into the cost basis, the discharge loss expensed
 *   inside `drawn x basis`. Adding it charges the same money twice.
 *
 * `variant: "memo"` marks both, every surface renders them apart from the cash
 * rows, and each carries its own reason on hover because the reasons differ.
 * Transfer's Ratchet Floor row is a memo for a third reason: it is a part of
 * the Demand row above it, not an amount beside it.
 *
 * ── Where the money comes from: the engine, never this file ───────────────
 * Every dollar figure on these rows is READ FROM A PERSISTED ENGINE COLUMN,
 * or -- Fuel, Held and the utility bill -- is a sum or difference of engine
 * totals that holds by the engine's own definitions. This module prices
 * nothing: no charge x buy, no discharge x sell, no multiplication at all.
 *
 * That is a deliberate architectural decision by the product owner: "the cards
 * should be the same math as what the engine produces. I don't want to do
 * different math from the back end to the front end." A component doing its
 * own cost arithmetic is what DataProcessor.jsx did, and it was deleted from
 * this branch because its formulas had silently drifted from the engine's.
 *
 * It is also the only correct option, for a reason worth recording because it
 * is not obvious and it defeated an earlier version of this work. A per-flow
 * purchase figure cannot be derived client-side, because not every kWh of
 * charge was bought:
 *
 *   - Result files carry `<device> To Storage (kWh)`, the energy a device sent
 *     directly into storage. Energy routed that way was never purchased.
 *   - It is NOT a rare edge case. Measured over every scenario in
 *     Grene.DT.sim Prod/projects, internal routing into storage is non-zero in
 *     five of eight, and the percentage is the share of that scenario's total
 *     charge spend that pricing every kWh at the buy rate would invent:
 *       husky-2040-prod   Battery1 643,338.3 kWh                      8.66%
 *       husky-2040        Solar 46,879.1 + BESS 4,256.9 kWh           6.26%
 *       husky-2040-ref    Battery1 35,387.4 kWh                       5.56%
 *       husky-2028        Solar 75.2 + BESS 342.9 kWh                 9.46%
 *       husky-2028-noev   Solar 111.1 kWh                            69.40%
 *     On husky-2040-prod that is $47,334 a year of energy the battery had
 *     already paid for, billed to the EVs a second time.
 *   - The split is NOT recoverable per device. `To Storage` records only that a
 *     device sent energy to storage, not which of the six EVs received it.
 *     With more than one storage device the attribution is unavailable at any
 *     accuracy, not merely approximate.
 *
 * The engine has the routing decomposition and can price only grid-sourced
 * charge correctly. This module cannot. So it reads columns and never computes.
 *
 * (The engine has since ruled -- 2026-08-29, see storageCharged below -- that a
 * storage device is charged for EVERY kWh it receives, at the buy price its
 * source is credited, so internal transfers net to zero across the cards. That
 * is the kind of decision that belongs to the engine, and it is why the rule
 * above holds: the ruling changed, and this file only had to read it.)
 *
 * ── Reading a figure that does not exist yet ──────────────────────────────
 * Several of the columns below are not persisted yet; a sim change is queued to
 * add them. Until then those cells are ABSENT, and absent renders an em-dash --
 * never a fabricated $0.00.
 *
 * KNOWN, AND ACCEPTED: a run simulated before 2026-08-28 shows an em-dash for
 * the Transfer card's total, and for the Transfer tab's Utility bill.
 *
 * The engine began writing `Transfer Import Cost ($)` and `Transfer Export
 * Revenue ($)` on 2026-08-28 (sim 83efad0, "Persist what each flow was worth").
 * The utility bill is built from exactly those two columns plus the billed
 * demand, so on an older result file it is not smaller, it is unknown, and the
 * card says so. Nothing errors: the sums report absence (columnSums.js), the
 * total resolves to null, and both surfaces render the em-dash through the
 * ordinary absent path.
 *
 * The number is not lost. `Transfer Cost ($)` has always been persisted, and
 * it is what the site-net line under the total carries; that line reports
 * status "incomplete" on these runs, because it cannot check itself without
 * the metered halves. Re-run the simulation and both fill in.
 *
 * That distinction is the whole point of this branch: a card showing $0.00
 * because data is missing is indistinguishable from one showing a real
 * measured zero, and a customer has already reported the fabricated kind as an
 * accounting bug. Presence is a null/empty and finite check, not truthiness, so
 * a genuine 0 still renders $0.00. The rule has exactly one definition and it
 * lives in summaryCardReadiness.js; this module imports it rather than
 * restating it.
 *
 * When the engine starts writing a column, wiring it up is a one-line edit:
 * change that entry's `status` to "persisted" and point `field` at whatever the
 * value lands on. The row, its layout and its formatting already work.
 */

import { hasCardValue } from "./summaryCardReadiness";

/* ── The column registry ───────────────────────────────────────────────────
 *
 * One place naming every per-flow money figure a card can show.
 *
 *   field    the property the value arrives on, in the metrics/summary object
 *            the card is handed
 *   column   the engine column that value comes from, named exactly
 *   status   "persisted" -> the engine writes it today, the cell populates
 *            "pending"   -> queued for the sim change, the cell shows an
 *                           em-dash and `hint` explains why
 *   hint     optional. The reason an absent cell is absent, replacing the one
 *            the status would otherwise imply. Needed where the status alone
 *            says the wrong thing -- see deviceDemand, where the engine writes
 *            the column but nothing reads it, so "re-run to populate it" is
 *            advice that cannot work
 *   meaning  what the figure must mean, precisely enough to implement against.
 *            This is the spec the sim change is written to, so the wording is
 *            load-bearing -- particularly "grid-sourced" and "to grid", which
 *            are the two places a plausible-looking implementation goes wrong.
 */
export const ENGINE_COST_COLUMNS = {
  storageCharged: {
    field: "chargedCost",
    column: "Storage Charge Cost ($)",
    source: "timeseries",
    status: "persisted",
    /* This said "GRID-SOURCED charge only" until the Held row was built on
     * top of it, and the Held derivation proved it wrong: on a week of
     * husky-2040-prod with a gas generator added, the six EV fleets' charge
     * cost came to $8,154 while the entire utility bill was $758 -- most of
     * their charge came from the generator, and it is in this column. */
    meaning:
      "Cost of every kWh the device received (<= 0), from the grid, on-site " +
      "production or another storage device alike, at the hour's buy price. " +
      "Energy from another storage device is credited back on the sender.",
  },
  storageDischarge: {
    field: "dischargeRevenue",
    column: "Storage Discharge Value ($)",
    source: "timeseries",
    status: "persisted",
    /* Named "Value", not "Revenue", and the row label follows suit.
     *
     * Only the exported leg of a discharge is cash. The rest displaced a
     * purchase, which is avoided cost -- worth the same to the bill, but not
     * money that changed hands. On husky-2040-prod the battery discharged
     * 1,098,712 kWh with none exported at all, so the entire figure there is
     * avoided cost, and calling it revenue would simply be wrong. */
    meaning:
      "Value of discharge: cash for the exported leg, avoided cost for the rest. " +
      "NOT revenue -- most of it never changed hands.",
  },
  /* Verified against the engine's own decomposition over husky-2040-prod
   * (8,784 hourly rows, 6 EVs): per EV, `Storage Cost ($)` equals
   * `EV Arbitrage ($)` + `EV Operating Cost ($)` to $0.0000, and
   * `EV Operating Cost ($)` equals -(EV Driving Energy x EV Cost Basis) to
   * within $1.45 on ~$100k, which is the rounding of a 5-decimal basis column.
   * It is the engine's accrual for energy the vehicle actually burned, which is
   * exactly what the memo line means. */
  storageDriving: {
    field: "operatingCost",
    column: "EV Operating Cost ($)",
    source: "timeseries",
    status: "persisted",
    meaning:
      "Energy the vehicle burned through the wheels, expensed at the running " +
      "inventory cost basis. Engine accrual, not a cash movement -- this is a " +
      "memo line and is excluded from the card total.",
  },
  /* The Losses row's money.
   *
   * ── Why it is a memo line and not a cash row ──────────────────────────────
   * It is a RECLASSIFICATION of money already inside `Storage Cost ($)`, not a
   * charge alongside it, and the engine's own note says so component by
   * component:
   *
   *   - the charging loss, Charge x (1 - eff_ch), was capitalised into the cost
   *     basis when the charge landed, so it is already inside
   *     `Storage Charge Cost ($)` and reaches `Storage Cost ($)` later as part
   *     of the basis of whatever is drawn;
   *   - the discharging loss, Discharge x (1 / eff_dis - 1), is already expensed
   *     inside the `inventory drawn x basis` term of `Storage Cost ($)`, because
   *     the inventory a discharge consumes exceeds the energy it delivers by
   *     exactly that much.
   *
   * So adding it into the card total would charge the same money twice. It sits
   * on the card because a device losing 118,318.6 kWh a year had no row saying
   * so, not because the total was missing a term.
   *
   * ── The zero that is real ─────────────────────────────────────────────────
   * Pre-simulation inventory carries a zero cost basis by site convention, so a
   * device can lose real kWh at a genuinely measured $0.00 -- Battery1's opening
   * 7,650 kWh on husky-2040-ref does exactly that, and on a daily view its early
   * hours show kWh of loss beside $0.00. That is a MEASURED zero and must render
   * "0", not an em-dash. It reaches the card as such because the worker probes
   * the energy and money columns separately, and because presence here is
   * `hasCardValue` -- a null/finite check, never truthiness. */
  storageLosses: {
    field: "lossCost",
    column: "<device> Storage Loss Cost ($)",
    source: "timeseries",
    status: "persisted",
    meaning:
      "What the lost energy cost (<= 0), valued the way the money was actually " +
      "spent: the charging loss at the price the energy was bought at, the " +
      "discharging and intrinsic losses at the inventory cost basis. A " +
      "reclassification of money already inside `Storage Cost ($)` -- this is a " +
      "memo line and is excluded from the card total.",
  },
  productionGenerated: {
    field: "generatedValue",
    column: "Production Generation Value ($)",
    source: "timeseries",
    status: "persisted",
    /* Until 2026-08-28 this excluded generation routed into storage, and the
     * row paired all the energy generated with the value of only what was
     * delivered. The engine now credits the producer for that leg at the buy
     * price and charges the receiving device the identical figure
     * (der_twin/constants.py, PRODUCTION_GENERATION_VALUE), so the energy and
     * the money on the row cover the same output. */
    meaning:
      "Value of everything generated, before fuel: self-consumed and stored " +
      "generation at the avoided buy rate, exported generation at the sell " +
      "rate. The receiving storage device is charged the stored leg at the " +
      "same price, so the transfer nets to zero across the cards.",
  },
  consumptionLoad: {
    field: "loadCost",
    column: "Building Load Load Cost ($)",
    source: "timeseries",
    status: "persisted",
    meaning:
      "Cost of serving building load. Persisted per meter and summed into " +
      "`Totals Consumption Cost ($)`; still needs surfacing per consumption " +
      "device, which the webapp cannot distinguish today.",
  },
  transferImport: {
    field: "importCost",
    column: "Transfer Import Cost ($)",
    source: "timeseries",
    status: "persisted",
    meaning:
      "Cash paid for energy imported from the grid (<= 0), priced interval by " +
      "interval at the buy rate in force. Must be the per-interval sum: on a " +
      "three-tier TOU tariff, total kWh x an averaged rate is wrong by up to 22% " +
      "(measured on husky-2040).",
  },
  transferExport: {
    field: "exportRevenue",
    column: "Transfer Export Revenue ($)",
    source: "timeseries",
    status: "persisted",
    meaning:
      "Cash earned for energy exported to the grid (>= 0), priced per interval at " +
      "the sell rate in force. Genuinely revenue, unlike the storage discharge " +
      "figure. Same per-interval requirement as import.",
  },
  /* Per-device demand attribution.
   *
   * DAILY SUMMARY ONLY -- this column is not in the timeseries, so it does not
   * arrive through metrics.worker.js like the others. It comes down the
   * daily-summary path (utils/demandCharge.js), which is where extraction for
   * it lives.
   *
   * Verified on husky-2040-prod stamp 082926_0805: the eight per-device values
   * sum to -876,447.26 over the year, which is exactly
   * `Production Demand ($)` 2,719.57 + `Storage Demand ($)` -879,166.83.
   *
   * SURFACED PER DEVICE. The reader is `sumDeviceAttributionForPeriod` in
   * utils/demandCharge.js; the Storage and Production device pages call it with
   * their chart's own ISO period keys and set `deviceDemand` from the result.
   *
   * It therefore no longer carries its own `hint`: with a reader in place, an
   * empty cell means the daily summary of THIS run has no attribution column
   * for THIS device, which is exactly what HINT_BY_STATUS.persisted describes
   * and which re-running a current sim does fix. The Consumption device card
   * supplies its own reason at the row instead, because its absence has a
   * different cause -- see buildConsumptionFlowRows. */
  deviceDemand: {
    field: "deviceDemand",
    column: "<device> Demand Attribution ($)",
    source: "daily-summary",
    status: "persisted",
    meaning:
      "This device's share of the monthly demand charge, attributed by " +
      "decomposing the interval that set the peak. Daily-summary sign " +
      "convention: a cost negative, a benefit positive, so a device that pulls " +
      "the peak down reads positive.",
  },
};

/* Shown on the Generated row: what its value covers, and where the price of
 * each leg comes from. It said the value left out whatever went into storage,
 * which stopped being true when the engine began crediting that leg
 * (2026-08-28). */
export const GENERATED_SCOPE_NOTE =
  "Value of everything generated: energy used on site or sent into storage " +
  "at the buy price it displaced, exports at the sell price. The fuel it " +
  "took is the row above.";

/* Why a cell is empty, shown on hover. Absent is not zero, and a user who sees
 * an em-dash should be able to find out why without reading the source.
 *
 * The two reasons are different and the hint says which: a column the engine
 * does not write at all, versus one it writes now but which this particular
 * result file predates. Both render an em-dash; only one of them is fixed by
 * re-running the simulation.
 *
 * A registry entry may carry its own `hint` and override both, for the third
 * case neither of these covers: the engine writes the column and this app does
 * not read it, where telling the user to re-run is simply wrong. */
const HINT_BY_STATUS = {
  persisted:
    "Not recorded in this simulation run: the engine writes this figure now, " +
    "but this result file predates the column. Re-run to populate it. Shown as " +
    "an em-dash rather than $0 so it cannot be mistaken for a measured zero.",
  pending:
    "Not recorded for this simulation run: the engine does not persist this " +
    "figure yet. Shown as an em-dash rather than $0 so it cannot be mistaken " +
    "for a measured zero.",
};

/**
 * Read a per-flow money figure off whatever object the card was handed.
 *
 * Returns `{ value, absent, hint }`. `absent` is true when the engine has not
 * supplied the figure -- either the column is not persisted yet, or this run
 * predates it. Never invents a 0.
 */
export function readEngineCost(source, key) {
  const spec = ENGINE_COST_COLUMNS[key];
  if (!spec) {
    return { value: null, absent: true, hint: undefined };
  }

  const raw = source?.[spec.field];
  if (hasCardValue(raw)) {
    return { value: raw, absent: false, hint: undefined };
  }

  return {
    value: null,
    absent: true,
    hint: `${spec.hint || HINT_BY_STATUS[spec.status] || HINT_BY_STATUS.pending} (engine column: ${spec.column})`,
  };
}

/* A row the card renders. `cost` is always the shape readEngineCost returns, so
 * a renderer never has to ask whether a figure is present -- it asks the row.
 *
 * `memoNote` is the dagger's tooltip on a memo row, and it is per-row rather
 * than one shared sentence because the two memo rows are excluded from the
 * total for DIFFERENT reasons: Driving because that energy was already paid for
 * when it was charged, Losses because its money is a reclassification of cash
 * already inside the total. One sentence covering both would have to be vague
 * enough to be true of each, which is how a reader ends up assuming the two
 * rows mean the same thing. */
const flowRow = ({
  key,
  label,
  power,
  energy,
  cost,
  variant = "flow",
  hint,
  memoNote,
}) => ({
  key,
  label,
  power,
  energy,
  cost,
  variant,
  hint,
  memoNote,
});

/* ── Fuel ──────────────────────────────────────────────────────────────────
 *
 * The cash a fuelled generator burned to produce its output. The engine writes
 * it per device as `<device> Fuel Cost ($)` and nets it into `Production Cost
 * ($)` before any card sees it, so the summary figure is the difference between
 * the two production columns the card already reads:
 *
 *     fuel = Production Generation Value ($) - Production Cost ($)
 *
 * Derived rather than read because the engine publishes no category total for
 * it, and the identity above is exact by construction -- Production Cost IS
 * generation value less fuel. Verified on the-farms DG-R E: 160,833 generated
 * less 102,677 fuel = 58,156, matching the Production Cost column to the cent.
 *
 * Start/stop cost comes along with it. The engine's identity is
 * Production Cost = Generation Value - Fuel - Startup/Shutdown, so the
 * difference is everything it cost to run the generator, and the row's hint
 * says so. On every run measured so far start/stop is $0.00 and the difference
 * equals the engine's `Fuel Cost ($)` column to the cent.
 *
 * productionFuelExact returns the figure whenever both inputs exist, zero
 * included, because the site-net line needs to know a zero is a zero.
 * productionFuelCost is the card's version: null when there is nothing to show,
 * because a site with no fuelled generator has generation value equal to
 * production cost, and a zero row on every solar card is noise. Both guard on
 * the inputs being real numbers, so a missing column produces no row rather
 * than a spurious one.
 */
function productionFuelExact(source = {}) {
  const generated = source?.generatedValue;
  const net = source?.totalCost;
  if (!hasCardValue(generated) || !hasCardValue(net)) return null;
  const fuel = Number(generated) - Number(net);
  return Number.isFinite(fuel) ? -fuel : null;
}

function productionFuelCost(source = {}) {
  /* `generatedValue` is the field ENGINE_COST_COLUMNS.productionGenerated
   * reads; `totalCost` is the category net the card's own total is built from.
   * Both are already on the object every production card is handed. */
  const fuel = productionFuelExact(source);
  return fuel === null || Math.abs(fuel) < 0.005 ? null : fuel;
}

/* The fuel row, cost-negative like every other money figure on every card.
 * Null in, null out, so a caller can hand it a site with no generator. */
const fuelRow = (cost) =>
  hasCardValue(cost)
    ? flowRow({
        key: "fuel",
        label: "Fuel",
        power: null,
        energy: null,
        cost: { value: cost, absent: false, hint: undefined },
        variant: "demand",
        hint:
          "What it cost to run the generator: the fuel it burned, plus any " +
          "start/stop cost it is configured with. Already netted into the " +
          "total, which is why the rows add up to it. It is on this card " +
          "only; the utility bill on Grid Transfer does not include it.",
      })
    : null;

/* ── Held ──────────────────────────────────────────────────────────────────
 * A battery and a generator are the same kind of device. Each buys an input --
 * off-peak electricity, or gas -- and turns it into an output worth more, and
 * each card reads the same way: input cost, output value, net. The one
 * difference is timing. A generator burns its fuel as it goes; a battery can
 * end the period still holding energy it bought. That energy has been paid for
 * (it is in the utility bill, or the fuel bill) but not yet used, so the engine
 * carries it as an asset at its running cost basis instead of expensing it,
 * and `Storage Cost ($)` leaves it out.
 *
 * Held is that asset's change over the period:
 *
 *     Held = Storage Cost - Charge Cost - Discharge Value
 *
 * positive when the period ends holding energy it bought, negative when it
 * drew down energy bought before it. With it a storage card closes the way the
 * production card does -- Charged + Discharge + Held = the engine's own total
 * -- and so does the site:
 *
 *     Production + Consumption + Storage  =  utility bill + fuel + held
 *
 * which is the reconciliation line under the Transfer card. Held is derived
 * FROM the engine total, never the other way round, so it cannot move a total.
 *
 * Measured on a week of husky-2040-prod with a 1.5 MW gas generator added
 * (battery, six EV fleets, solar, ratcheted demand): site net -13,715.38,
 * utility bill -758.37, fuel -13,622.82, held +666.34, leaving -0.53 -- the
 * engine rounds each hourly cost column to the cent, 168 times. */
function storageHeldExact(source = {}) {
  const value = source?.value;
  const charged = source?.chargedCost;
  const discharged = source?.dischargeRevenue;
  if (!hasCardValue(value) || !hasCardValue(charged) || !hasCardValue(discharged)) {
    return null;
  }
  const held = Number(value) - Number(charged) - Number(discharged);
  return Number.isFinite(held) ? held : null;
}

function storageHeldCost(source = {}) {
  const held = storageHeldExact(source);
  if (held === null || Math.abs(held) < 0.005) return null;
  return held;
}

const heldRow = (cost) =>
  hasCardValue(cost)
    ? flowRow({
        key: "held",
        label: "Held",
        power: null,
        energy: null,
        cost: { value: cost, absent: false, hint: undefined },
        variant: "demand",
        hint:
          "Energy bought in this period and still in storage at its end, at " +
          "its cost -- less energy bought before the period and used in it. " +
          "It is paid for but not yet used, so it is carried, not expensed.",
      })
    : null;

/* The demand row is money-only and sits on its own line rather than being
 * folded into another row, because the product owner asked for it that way and
 * because it is the one charge on the card that no single flow caused. */
const demandRow = (cost, { hint } = {}) =>
  flowRow({
    key: "demand",
    label: "Demand",
    power: null,
    energy: null,
    cost,
    variant: "demand",
    hint,
  });

/* Demand on a summary card comes from the category attribution the engine
 * already writes; on a per-device card it comes from the registry, which today
 * reports it absent. Both go through the same row so the layouts agree.
 *
 * Returns null when the tariff has no demand charge at all. That is a third
 * state, distinct from both "absent" and "zero": there is no such charge to
 * attribute, so the card omits the row rather than showing an em-dash that
 * would imply a figure went missing. It matches what these cards did before
 * this change, where the demand block appeared only under hasDemandCharge. */
const DEMAND_NOT_ATTRIBUTED =
  "Demand charge attribution was not recorded for this simulation run";

function summaryDemandCost(demandData, field) {
  if (!demandData?.hasDemandCharge) return null;

  if (!demandData?.hasAttribution) {
    return { value: null, absent: true, hint: DEMAND_NOT_ATTRIBUTED };
  }

  const raw = demandData[field];
  return hasCardValue(raw)
    ? { value: raw, absent: false, hint: undefined }
    : { value: null, absent: true, hint: DEMAND_NOT_ATTRIBUTED };
}

/* A summary demand row, or null when the tariff carries no demand charge. */
function optionalDemandRow(demandData, field) {
  const cost = summaryDemandCost(demandData, field);
  return cost ? demandRow(cost) : null;
}

/* A per-device demand row, or null when the tariff carries no demand charge.
 *
 * The same third state optionalDemandRow uses. Ungated, a site whose tariff has
 * no demand charge AT ALL still got a Demand row with an em-dash in it,
 * claiming a figure had gone missing when there was never a charge to
 * attribute. */
function optionalDeviceDemandRow(source, demandData) {
  if (!demandData?.hasDemandCharge) return null;
  return demandRow(readEngineCost(source, "deviceDemand"));
}

/* ── Per-device totals ─────────────────────────────────────────────────────
 *
 * A device card's total is formed exactly the way a summary card's is -- the
 * engine's energy figure for the card, plus that card's demand charge, through
 * the same cardNetTotal -- so the two surfaces cannot end up meaning different
 * things by the same big number.
 *
 * Both halves are returned together because they are one decision: `total` is
 * the figure and `includesDemand` is whether a demand term went into it, which
 * is what makes the label read "Net Total / energy + demand" rather than
 * "Total". Returning them separately invites a caller to compute one and forget
 * the other, and a card claiming "energy + demand" over a bare energy figure is
 * precisely the silent redefinition the label exists to prevent.
 */

/** One device's share of the demand charge, in the cost-negative form. */
function deviceDemandContribution(source, demandData) {
  if (!demandData?.hasDemandCharge) return NO_DEMAND_CHARGE;
  return hasCardValue(source?.deviceDemand)
    ? Number(source.deviceDemand)
    : null;
}

/**
 * The TOTAL for a Storage or Production device card: the engine's per-device
 * energy cost plus that device's demand attribution.
 *
 * Absence behaves as it does on the summary cards. No demand charge on the
 * tariff and the total is the energy figure alone under a plain "Total". A
 * demand charge with no attribution recorded for this device and the net cannot
 * be formed, so the total is an em-dash rather than the energy figure dressed
 * up as a net.
 */
export function deviceCardTotal(energyCost, source, demandData) {
  return {
    total: cardNetTotal(energyCost, deviceDemandContribution(source, demandData)),
    includesDemand: Boolean(demandData?.hasDemandCharge),
  };
}

/* ── The Consumption device card's demand term ─────────────────────────────
 *
 * OWNER DECISION, and the reason this branch exists at all: it was deleted once
 * as "a row that can only ever be blank", and it is not to be deleted again.
 *
 * The figure it shows is the CATEGORY attribution, `Consumption Demand ($)` --
 * the very same figure, from the very same period slice, that the Summary tab's
 * Consumption card shows. That is a read of an engine figure rather than a
 * computation: the engine attributes demand to Consumption as a category
 * through the power-balance identity, and building load is today the whole of
 * that category -- one consumption device, so the category value IS the device
 * value, exactly.
 *
 * Two things follow, and both are deliberate:
 *
 *   - PER-DEVICE COLUMNS WIN WHEN THEY EXIST. The device pages already call
 *     `sumDeviceAttributionForPeriod` for the consumption device by name. The
 *     engine writes no such column today, so the lookup finds nothing and the
 *     category figure serves. If it ever starts writing one, `deviceDemand`
 *     populates and supersedes the category figure here with no webapp change.
 *     The sysconfig schema already carries `controllable_loads`, so a second
 *     consumption device is a question of when, not whether.
 *
 *   - THE SITE FIGURE APPEARS ON EVERY CARD while there is more than one
 *     consumption device and no per-device column. That is the same property
 *     `price`, `maxPower` and `loadCost` on that page already have, and it is
 *     documented at the page rather than hidden here.
 *
 * An em-dash remains the answer when the category figure itself is absent --
 * a run predating the attribution columns -- which is exactly what the Summary
 * Consumption card does with the same input.
 */
function consumptionDeviceDemandRow(source, demandData) {
  if (!demandData?.hasDemandCharge) return null;
  return hasCardValue(source?.deviceDemand)
    ? demandRow(readEngineCost(source, "deviceDemand"))
    : optionalDemandRow(demandData, "consumptionDemand");
}

/** The Consumption device card's demand term, matching the row above it. */
function consumptionDeviceDemandContribution(source, demandData) {
  if (!demandData?.hasDemandCharge) return NO_DEMAND_CHARGE;
  return hasCardValue(source?.deviceDemand)
    ? Number(source.deviceDemand)
    : categoryDemandContribution(demandData, "consumptionDemand");
}

/**
 * The TOTAL for a Consumption device card.
 *
 * Built from the same two named figures as the Summary Consumption card, and
 * through the same cardNetTotal, so the two pages cannot disagree about what
 * consuming energy at this site cost.
 */
export function consumptionDeviceCardTotal(energyCost, source, demandData) {
  return {
    total: cardNetTotal(
      energyCost,
      consumptionDeviceDemandContribution(source, demandData),
    ),
    includesDemand: Boolean(demandData?.hasDemandCharge),
  };
}

/* Distinguishes "this tariff has no demand charge" (add nothing) from "the
 * demand figure is missing" (the net cannot be formed). */
const NO_DEMAND_CHARGE = Symbol("no demand charge");

/* ── The card total: energy cost + that card's demand charge ───────────────
 *
 * The engine's category figure -- `Storage Cost ($)`, `Transfer Cost ($)`,
 * `Production Cost ($)`, `Consumption Cost ($)` -- is the ENERGY value. The
 * total is that plus the card's demand charge, so the big number is the net the
 * card actually costs.
 *
 * Still two NAMED figures, never a sum of the rows on screen. The rows are
 * derived to add up to it -- Held and Fuel are computed FROM these figures --
 * never the other way round, so no row can move a total. Nothing here adds
 * Charged to Discharge, and the memo lines stay out.
 *
 * ── Sign, which is where this goes wrong if you are not careful ───────────
 * The two inputs do NOT share a convention, and adding them naively moves the
 * Grid total the wrong way by twice the demand charge:
 *
 *   the per-category attributions are cost-NEGATIVE, like every other money
 *   column in the daily summary, so they add directly;
 *
 *   the billed `Demand Charge ($)` is stored as a POSITIVE MAGNITUDE, so the
 *   Grid card must add its NEGATION.
 *
 * The two are tied together by the identity demandCharge.js documents and
 * demandCharge.test.js pins: the four attributions sum to minus the billed
 * charge. Verified on stamp 082926_0418 over the full year --
 * 2,719.57 + (-362,433.79) + (-879,166.83) + 0.00 = -1,238,881.05, residual
 * 0.0000 against a billed charge of 1,238,881.05.
 *
 * ── Unattributed demand, and why the cards stop adding up when it is not 0 ──
 * The Grid card's demand term is the WHOLE billed charge, which already
 * contains the unattributed remainder. The Ratchet Floor row shows that
 * remainder as a decomposition of the same money and is therefore a memo line,
 * NOT added again -- doing so would double-count it.
 *
 * The three category cards add only their own attributed shares. So when the
 * ratchet floor binds:
 *
 *     Production net + Consumption net + Storage net
 *       = site net cost - unattributedDemand
 *
 * where the site net cost is the line under the Transfer card. That is correct
 * rather than a defect -- the floor is a minimum billing quantity no device
 * caused, so it belongs to no category -- but it does mean the three cards stop
 * summing to the site net on exactly those runs. On stamp 082926_0418
 * unattributed is 0.00 and they reconcile exactly, which is precisely why this
 * is written down: it reconciles today and would not on a ratcheted account,
 * with nothing on screen to explain the gap except the Ratchet Floor row on the
 * Transfer card.
 *
 * ── Absence ───────────────────────────────────────────────────────────────
 * A net needs both halves. If the tariff has no demand charge there is no
 * second term and the total is the energy figure alone. If there IS a demand
 * charge but this run did not record the attribution, the net is not
 * computable, and the card shows an em-dash rather than the energy figure
 * dressed up as a net -- which would be a fabricated total of exactly the kind
 * this branch exists to prevent.
 */
function cardNetTotal(energyCost, demandContribution) {
  if (!hasCardValue(energyCost)) return null;
  if (demandContribution === NO_DEMAND_CHARGE) return Number(energyCost);
  if (!hasCardValue(demandContribution)) return null;
  return Number(energyCost) + Number(demandContribution);
}

/** One category card's share of the demand charge, in the cost-negative form. */
function categoryDemandContribution(demandData, field) {
  if (!demandData?.hasDemandCharge) return NO_DEMAND_CHARGE;
  if (!demandData?.hasAttribution) return null;
  return hasCardValue(demandData[field]) ? Number(demandData[field]) : null;
}

/* The Grid card's demand term: the whole billed charge, negated into the
 * cost-negative convention the energy figure already uses. Grid is the card the
 * bill lands on, so it takes the charge itself rather than an attributed
 * share. */
function billedDemandContribution(demandData) {
  if (!demandData?.hasDemandCharge) return NO_DEMAND_CHARGE;
  return hasCardValue(demandData.value) ? -Number(demandData.value) : null;
}

/* The dagger's tooltip, one per memo row.
 *
 * Both rows are outside the total, but not for the same reason, and a single
 * sentence covering both would have to be vague enough to be true of each --
 * which is precisely how a reader concludes the two rows mean the same thing.
 * Driving is energy already paid for; Losses is money already counted. */
export const DRIVING_MEMO_NOTE =
  "Driving is shown for reference only and is not included in the total — " +
  "that energy was already paid for when it was charged.";

export const LOSSES_MEMO_NOTE =
  "Losses is shown for reference only and is not included in the total — its " +
  "cost is a reclassification of money already inside the total, not an " +
  "additional charge.";

/* The generic fallback, and the sentence the card's own footnote is built from.
 *
 * It states the POSITIVE rule as well as the negative one. With two daggered
 * rows on a card, the contrast alone implies the undaggered rows are the
 * addends -- and since the Held and Fuel rows landed, they are: on every card
 * the rows without a dagger sum to the total. This used to say the opposite
 * ("the total is the engine's own figure, never a sum of the rows"), which was
 * true while a battery's unused energy and a generator's fuel had no row. The
 * total is still formed from the engine's own figures and never from the rows;
 * the rows are derived to agree with it. */
export const MEMO_FOOTNOTE =
  "Shown for reference only and not included in the total. The rows without " +
  "a dagger add up to the total.";

/* The caption under the table. Short enough for a 10px line, and carrying the
 * same two halves as MEMO_FOOTNOTE: what the dagger excludes, and what the
 * rest add up to. */
export const MEMO_CAPTION = "Not included in the total";
export const TOTAL_CAPTION = "the other rows add up to it";

/* Transfer names its own total. The card carries only what crossed the
 * utility meter -- import, export, demand -- so its total is the utility bill,
 * and exactly the sum of its rows. It was briefly "Total bill of operation"
 * with the generator's fuel on it as well, which put the same fuel in two card
 * totals, Transfer's and Production's. Fuel now lives on Production alone, and
 * the site's all-in cost is the line under this total: SITE_NET_LABEL. */
export const TRANSFER_TOTAL_LABEL = "Utility bill";

/* The line under the Transfer total that connects the cards:
 * site net cost = utility bill + generator fuel + held in storage.
 * See buildSiteReconciliation. */
export const SITE_NET_LABEL = "Site net cost";

/* The Ratchet Floor row's dagger. It is a part of the Demand row above it, so
 * adding it to the total would bill the same money twice. */
export const RATCHET_FLOOR_MEMO_NOTE =
  "Ratchet Floor is part of the Demand row above, shown for reference and not " +
  "added again: the share of the billed demand charge no device caused.";

/* ── Storage ───────────────────────────────────────────────────────────────
 *
 * The exits a device's energy can take, one row each:
 *
 *     Charged                        in through the plug
 *     Discharge                      back out through the plug
 *     Held                           still inside at the end: bought, unused
 *     Driving    † EV only           out through the wheels: work performed
 *     Losses     †                   never came out at all: waste
 *
 * and the energy closes against the engine's own identity,
 * `Charged - Discharged - Driving - Losses - dStored = 0`, verified per device
 * on husky-2040-ref. Held is the money of the dStored term, and it is what
 * makes the money close as well: Charged + Discharge + Held is the card's
 * energy total, exactly.
 *
 * ── Driving, not Consumed ─────────────────────────────────────────────────
 * The rename is the product owner's and it is the reason the two rows can sit
 * beside each other at all. An EV's driving energy is WORK THE ASSET PERFORMED;
 * losses are WASTE. "Consumed" covers both readings, so a card carrying both
 * rows under that word would say the same thing about a vehicle that hauled
 * containers and a battery that leaked. The engine makes the same distinction
 * in the same words (der_twin/constants.py, STORAGE_LOSSES_COL: "NOT
 * 'Consumed', and the distinction is the owner's").
 *
 * The money is unchanged by the rename: the row still reads
 * `EV Operating Cost ($)`, which is the engine's accrual for energy burned
 * through the wheels.
 *
 * ── Why BOTH of them are memo lines ───────────────────────────────────────
 * Neither may enter the card total, and both are already inside the engine's
 * `Storage Cost ($)`:
 *
 *   Driving is expensed there directly -- verified as
 *   `Storage Cost` == `EV Arbitrage` + `EV Operating Cost` to $0.0000.
 *
 *   The loss cost is a RECLASSIFICATION of money already in it: the charging
 *   loss was capitalised into the cost basis, the discharge loss is expensed
 *   inside `drawn x basis`. See ENGINE_COST_COLUMNS.storageLosses.
 *
 * Adding either into the total double-counts. `variant: "memo"` marks both,
 * every surface renders them apart from the cash rows, and each carries its own
 * reason on the dagger because the two reasons differ.
 *
 * ── Why the undaggered rows now add up ────────────────────────────────────
 * With two daggered rows out of four, the contrast made a claim the card was
 * not entitled to make: that the two UNdaggered rows added up to the total.
 * They did not, and the footnote had to say "no row is an addend" to stop a
 * reader trying. The missing row was Held -- the energy bought and not yet
 * used, or used from before the period -- and with it on the card the claim
 * the dagger implies is simply true. The footnote now says so.
 *
 * The total is still a NAMED engine figure that no row can move -- see
 * cardNetTotal above, and the test that perturbs every row cost and asserts
 * the total does not move. Held is derived from the total, not the reverse.
 * The two memo rows stay adjacent under one separator, so they read as one
 * block apart from the rows that add up.
 */

/** The Losses row, identical on a battery and an EV.
 *
 * Same shape for both kinds because the engine defines the column once for
 * both: `<device>-Losses (kWh)` carries no device-type token, unlike the
 * charge/discharge columns. A battery and an EV lose energy the same way.
 *
 * `hasLosses` false means the result file predates the column, so the
 * quantities are UNKNOWN rather than zero -- an em-dash, never a 0 that would
 * read as "this device lost nothing". */
function lossesRow(source) {
  return flowRow({
    key: "losses",
    label: "Losses",
    power: { value: source.lossesMax, unit: "kW" },
    energy: { value: source.lossesTotal, unit: "kWh" },
    cost: readEngineCost(source, "storageLosses"),
    variant: "memo",
    hint: source.hasLosses
      ? undefined
      : "Loss energy was not recorded for this simulation run",
    memoNote: LOSSES_MEMO_NOTE,
  });
}

/** The Driving row's hint, on the per-device card and on the summary alike.
 *
 * `hasConsumed` false means nothing recorded driving energy for this source --
 * either the result file predates the column, or there are no vehicles here at
 * all. Either way the quantities are UNKNOWN rather than zero, and the row says
 * so instead of showing a bare daggered "Driving - - -" that a reader cannot
 * tell from a recorded nothing. It is the same distinction lossesRow draws from
 * `hasLosses` one row down.
 *
 * The summary card gets the flag from storageSummaryFlowMoney in
 * OverlayChart.jsx, which ORs it across the SELECTED devices exactly as it does
 * hasLosses -- so hiding the last EV takes the claim down with it. */
const drivingHint = (source) =>
  source?.hasConsumed
    ? undefined
    : "Driving energy was not recorded for this simulation run";

/** Per-device storage card. `metrics` is one entry of the worker's output.
 *
 * `demandData` is the site's demand-charge summary; the Demand row appears only
 * when it says the tariff has a demand charge. */
export function buildStorageDeviceFlowRows(
  metrics = {},
  { isEV = false, demandData } = {},
) {
  const rows = [
    flowRow({
      key: "charged",
      label: "Charged",
      power: { value: metrics.chargeMax, unit: "kW" },
      energy: { value: metrics.chargeTotal, unit: "kWh" },
      cost: readEngineCost(metrics, "storageCharged"),
    }),
    flowRow({
      key: "discharge",
      label: "Discharge",
      power: { value: metrics.dischargeMax, unit: "kW" },
      energy: { value: metrics.dischargeTotal, unit: "kWh" },
      cost: readEngineCost(metrics, "storageDischarge"),
    }),
    heldRow(storageHeldCost(metrics)),
  ];

  if (isEV) {
    /* Runs predating the sim's two-exit accounting have no driving column at
     * all and still fold driving into EV Discharge. `hasConsumed` false means
     * the quantities are unknown, not zero -- an em-dash, never a 0 that would
     * read as "this vehicle never drove".
     *
     * The worker's field names still say "consumed"; only the row the user
     * reads was renamed. The registry's `field` indirection is what absorbs
     * that, and renaming the worker's output would be a change to a contract
     * three other surfaces read, for no gain the card can see. */
    rows.push(
      flowRow({
        key: "driving",
        label: "Driving",
        power: { value: metrics.consumedMax, unit: "kW" },
        energy: { value: metrics.consumedTotal, unit: "kWh" },
        cost: readEngineCost(metrics, "storageDriving"),
        variant: "memo",
        hint: drivingHint(metrics),
        memoNote: DRIVING_MEMO_NOTE,
      }),
    );
  }

  /* Last of the four exits, and on both device kinds -- an EV gets
   * Charged / Discharge / Driving / Losses, a battery
   * Charged / Discharge / Losses. It follows Driving so the two memo rows are
   * adjacent and read as one block. */
  rows.push(lossesRow(metrics));

  /* Null when the tariff carries no demand charge, and dropped rather than
   * rendered: there is no figure to be missing, so there is no row. */
  const deviceDemandRow = optionalDeviceDemandRow(metrics, demandData);
  rows.push(deviceDemandRow);

  return rows.filter(Boolean);
}

/** Summary Storage card. `summary` is the aggregate OverlayChart publishes. */
export function buildStorageSummaryFlowRows(summary = {}, demandData) {
  /* The aggregate series signs charging negative, so the summary's minPower is
   * the charge extreme and maxPower the discharge extreme. That is the mapping
   * the card used before this change; it is preserved rather than rederived. */
  const rows = [
    flowRow({
      key: "charged",
      label: "Charged",
      power: { value: summary.minPower, unit: "kW" },
      energy: { value: summary.chargeTotal, unit: "kWh" },
      cost: readEngineCost(summary, "storageCharged"),
    }),
    flowRow({
      key: "discharge",
      label: "Discharge",
      power: { value: summary.maxPower, unit: "kW" },
      energy: { value: summary.dischargeTotal, unit: "kWh" },
      cost: readEngineCost(summary, "storageDischarge"),
    }),
    heldRow(storageHeldCost(summary)),
    flowRow({
      key: "driving",
      label: "Driving",
      power: { value: summary.consumedMax, unit: "kW" },
      energy: { value: summary.consumedTotal, unit: "kWh" },
      cost: readEngineCost(summary, "storageDriving"),
      variant: "memo",
      hint: drivingHint(summary),
      memoNote: DRIVING_MEMO_NOTE,
    }),
    /* The site's Losses row. The engine writes site-level forms of both columns
     * in its `Totals` group, but this card is built from the per-device figures
     * the worker already returned, because the summary card HONOURS DEVICE
     * SELECTION -- a Totals column would keep reporting the whole site after the
     * user hid half of it. Same reasoning, and the same aggregation helper, as
     * the Charged and Discharge money beside it. */
    lossesRow(summary),
    optionalDemandRow(demandData, "storageDemand"),
  ];

  return rows.filter(Boolean);
}

/* ── Production ────────────────────────────────────────────────────────────
 *
 * Production has one physical flow: energy generated on site. The engine does
 * record where it went (To Load / To Storage / To Export), but it records only
 * the ENERGY split -- there is no per-destination power and no per-destination
 * money -- so splitting the card into three destination rows would produce two
 * em-dashes out of every three cells and say less than one honest row does.
 * One "Generated" row, with the destination split left to the chart.
 */
export function buildProductionFlowRows(source = {}, demandData, { perDevice = false } = {}) {
  /* Input before output, as on the storage cards (Charged, then Discharge):
   * a generator is the same kind of device as a battery -- it buys an input
   * and sells an output worth more -- and the two cards read the same way. */
  return [
    fuelRow(productionFuelCost(source)),
    flowRow({
      key: "generated",
      label: "Generated",
      power: { value: source.maxPower, unit: "kW" },
      energy: { value: source.totalProduction, unit: "kWh" },
      cost: readEngineCost(source, "productionGenerated"),
      hint: GENERATED_SCOPE_NOTE,
    }),
    perDevice
      ? optionalDeviceDemandRow(source, demandData)
      : optionalDemandRow(demandData, "productionDemand"),
  ].filter(Boolean);
}

/* ── Consumption ───────────────────────────────────────────────────────────
 *
 * One flow: energy the building drew. Labelled "Load" rather than "Consumed" on
 * purpose. Storage's version of that row is now "Driving", so the collision the
 * name originally avoided is gone -- but the reason to keep "Load" is not: this
 * is a real cash cost that IS in the total, whereas both of Storage's
 * energy-out-the-far-side rows are memo lines that are not. "Consumed" would
 * invite a reader to treat them as the same kind of number, which is the same
 * mistake the Driving / Losses split exists to prevent one card over.
 */
export function buildConsumptionFlowRows(
  source = {},
  demandData,
  { perDevice = false } = {},
) {
  return [
    flowRow({
      key: "load",
      label: "Load",
      power: { value: source.maxPower, unit: "kW" },
      energy: { value: source.totalAreaUnderCurve, unit: "kWh" },
      cost: readEngineCost(source, "consumptionLoad"),
    }),
    /* Both surfaces show a demand row, and on this card they show the SAME
     * FIGURE: the category attribution `Consumption Demand ($)`.
     *
     * The per-device branch is not a second definition of the quantity, it is a
     * layering -- a per-device column supersedes the category figure the moment
     * the engine writes one, and until then the category figure is the device's
     * figure because building load is the whole category. The full reasoning,
     * and why this row must not be deleted again as unfillable, is on
     * consumptionDeviceDemandRow above.
     *
     * Both branches gate on the tariff having a demand charge, so a site with
     * no demand charge gets no row on either surface. */
    perDevice
      ? consumptionDeviceDemandRow(source, demandData)
      : optionalDemandRow(demandData, "consumptionDemand"),
  ].filter(Boolean);
}

/* ── Transfer ──────────────────────────────────────────────────────────────
 *
 * What crossed the utility meter, and nothing else: energy bought from the
 * grid, energy sold to it -- two transactions at separate prices -- and the
 * demand charge. The card's total is the utility bill, the sum of these rows;
 * see transferUtilityBill.
 *
 * Transfer's demand row is the whole billed charge, not an attributed share --
 * this is the card the bill lands on -- shown in the cost-negative convention
 * every other money figure uses, which is to say negated out of the positive
 * magnitude the engine stores it as.
 *
 * The ratchet floor gets its own row when it binds. Under a ratcheted tariff
 * the bill is rate x max(measured peak, ratchet floor); when the floor binds,
 * the excess over the measured peak is a minimum billing quantity no device
 * caused, so it is attributed to no category. It is PART of the Demand row,
 * so it is a memo line: shown, daggered, not added again. It appears only when
 * it is non-zero, so an unfloored account sees the layout it saw before.
 *
 * Generator fuel is not on this card. It is not paid to the utility, and it
 * is already in the Production card's total; buildSiteReconciliation is where
 * the two meet.
 */
export function buildTransferFlowRows(source = {}, demandData) {
  return [
    flowRow({
      key: "import",
      label: "Import",
      power: { value: source.minPower, unit: "kW" },
      energy: { value: source.totalImport, unit: "kWh" },
      cost: readEngineCost(source, "transferImport"),
    }),
    flowRow({
      key: "export",
      label: "Export",
      power: { value: source.maxPower, unit: "kW" },
      energy: { value: source.totalExport, unit: "kWh" },
      cost: readEngineCost(source, "transferExport"),
    }),
    demandData?.hasDemandCharge
      ? demandRow({
          /* The NEGATION of the billed magnitude, taken from the same
           * expression that forms this card's total, so the row and the total
           * it sits above cannot drift apart in sign.
           *
           * The billed `Demand Charge ($)` is stored as a positive magnitude
           * (see the sign note above cardNetTotal); every other money figure on
           * every card is cost-negative -- the three category Demand rows, and
           * the Ratchet Floor row immediately below this one, which carries the
           * attribution set's own already-negative remainder. Passed through
           * raw, this was the single row that read +1,238,881.05 under a
           * VALUE ($) heading while the same charge read -879,166.83 one card
           * over, sitting above a total it had made more negative.
           *
           * The absence guard stays on the RAW value: `-Number(null)` is -0,
           * which is finite, so testing the negation would report a missing
           * column as a measured zero. */
          value: billedDemandContribution(demandData),
          absent: !hasCardValue(demandData.value),
        })
      : null,
    ratchetFloorRow(demandData),
  ].filter(Boolean);
}

/* The unattributed remainder of a ratcheted demand charge, or null when the
 * tariff is unfloored, the run recorded no attribution, or the floor did not
 * bind. Only a non-zero floor is worth a row. */
function ratchetFloorRow(demandData) {
  if (!demandData?.hasDemandCharge || !demandData?.hasAttribution) return null;

  const floor = Number(demandData.unattributedDemand);
  if (!Number.isFinite(floor) || floor === 0) return null;

  return flowRow({
    key: "ratchet-floor",
    label: "Ratchet Floor",
    power: null,
    energy: null,
    cost: { value: floor, absent: false },
    variant: "memo",
    hint:
      "The part of the demand charge no device caused: the tariff billed above " +
      "the measured peak",
    memoNote: RATCHET_FLOOR_MEMO_NOTE,
  });
}

/**
 * The Transfer card's total: the utility bill for the period.
 *
 * Import cost + export revenue + the billed demand charge -- the rows on the
 * card, and exactly their sum. Both cash columns are required: a run missing
 * one has a bill that is unknown, not smaller, so it is an em-dash. The demand
 * term follows cardNetTotal's rules -- a tariff with no demand charge adds
 * nothing, a demand charge with no recorded figure leaves the total absent.
 *
 * Exported because the Transfer tab shows the same figure for its own period,
 * and one definition is what stops the two tabs disagreeing.
 */
export function transferUtilityBill(transferData, demandData) {
  const importCost = transferData?.importCost;
  const exportRevenue = transferData?.exportRevenue;
  if (!hasCardValue(importCost) || !hasCardValue(exportRevenue)) return null;
  return cardNetTotal(
    Number(importCost) + Number(exportRevenue),
    billedDemandContribution(demandData),
  );
}

/* ── The site net, and the line that connects the cards ───────────────────
 *
 * Transfer's total is the utility bill. The site's net cost -- what running it
 * cost, all in -- is the engine's own `Transfer Cost ($)` (Production +
 * Consumption + Storage) plus the billed demand, and it is the utility bill
 * plus the two places money turned into energy on site without crossing the
 * meter:
 *
 *     Site net cost = utility bill + generator fuel + held in storage
 *
 * Each term is read or derived on its own -- the site net from `Transfer Cost
 * ($)`, the bill from the two metered cash columns, fuel from the production
 * totals, held from the storage totals -- so the line is a CHECK, not a
 * restatement: nothing here is solved for. It closes because the engine prices
 * every internal transfer (generation into load or storage, storage into
 * storage) at the same buy price on both sides, so all of them cancel and only
 * the meter, the fuel and the unused inventory are left. Demand is on both
 * sides and cancels exactly.
 *
 * Measured on a week of husky-2040-prod with a 1.5 MW gas generator added:
 * -13,715.38 = -758.37 - 13,622.82 + 666.34, leaving -0.53 -- the engine
 * rounding each hourly cost column to the cent, 168 times. (Solved with HiGHS
 * standing in for GLPK; this is accounting, so it holds for any schedule.)
 *
 * `status`:
 *   "closed"        every term known, residual within rounding
 *   "unreconciled"  every term known, residual beyond it -- carried as a term
 *                   of its own, so the line on screen still adds up and the
 *                   gap is visible rather than absorbed
 *   "incomplete"    a term this run did not record; it shows an em-dash and the
 *                   line makes no claim to close
 *
 * A term that is exactly zero -- no generator, no storage -- is left off the
 * line rather than shown as 0; the utility bill always shows.
 *
 * The inputs must be WHOLE-SITE figures. The Charts tab's cards honour device
 * selection, and a site net cannot be checked against a subset of the site, so
 * the caller passes the unfiltered production and storage summaries.
 */
const RECONCILIATION_TOLERANCE_SHARE = 0.01;
const RECONCILIATION_TOLERANCE_FLOOR = 1;

export function buildSiteReconciliation({
  transferData,
  productionData,
  storageData,
  demandData,
} = {}) {
  const total = cardNetTotal(
    transferData?.value,
    billedDemandContribution(demandData),
  );
  if (!hasCardValue(total)) return null;

  const utility = transferUtilityBill(transferData, demandData);
  const fuel = productionFuelExact(productionData);
  const held = storageHeldExact(storageData);

  const shown = (value) => value === null || Math.abs(value) >= 0.005;
  const parts = [
    { key: "utility", label: "utility bill", value: utility },
    shown(fuel) ? { key: "fuel", label: "generator fuel", value: fuel } : null,
    shown(held) ? { key: "held", label: "held in storage", value: held } : null,
  ].filter(Boolean);

  if (utility === null || fuel === null || held === null) {
    return { label: SITE_NET_LABEL, total, parts, residual: null, status: "incomplete" };
  }

  /* Rounding grows with the number of hours in the period, and so does the
   * money, so the tolerance is a share of the terms' combined size. 1% is
   * roughly 300 times the measured rounding (0.0035% of the terms on the week
   * above) and far below any real mismatch between the columns. */
  const residual = total - (utility + fuel + held);
  const scale = Math.abs(utility) + Math.abs(fuel) + Math.abs(held);
  const tolerance = Math.max(
    RECONCILIATION_TOLERANCE_FLOOR,
    RECONCILIATION_TOLERANCE_SHARE * scale,
  );

  if (Math.abs(residual) <= tolerance) {
    return { label: SITE_NET_LABEL, total, parts, residual, status: "closed" };
  }

  return {
    label: SITE_NET_LABEL,
    total,
    parts: [
      ...parts,
      { key: "unreconciled", label: "unreconciled", value: residual },
    ],
    residual,
    status: "unreconciled",
  };
}

/* ── The four summary cards ────────────────────────────────────────────────
 *
 * Both summary surfaces -- the Power Flow diamond (CardsPage) and the Charts
 * tab's flat row (CardsRow) -- render the same four cards off the same context.
 * They previously carried a verbatim copy of this list each, which is two
 * places for a label, a field name or a row set to drift. One definition now;
 * the surfaces differ only in layout and test-id prefix.
 *
 * `price` becomes the TOTAL row, and is never summed from `rows`. On three
 * cards it is the engine's own category total plus that card's demand; on
 * Transfer it is the utility bill, formed from the two metered cash columns
 * and the billed demand -- the same three figures as its rows.
 *
 * `reconciliation` is Transfer's alone: the site-net line under its total.
 * `siteProductionData` and `siteStorageData` feed it, and default to the card
 * figures, which on the Power Flow tab already cover the whole site. The
 * Charts tab passes its unfiltered summaries, because its cards follow device
 * selection and the site net is a whole-site figure.
 *
 * ── title vs displayTitle ─────────────────────────────────────────────────
 * `title` is the IDENTIFIER and never changes. It resolves per-card visibility
 * (`summaryCardVisibility[title]`), drives the card toggle, becomes the CSS
 * class and the test id, selects the power-flow animation branch
 * (`title === "Transfer"` in CardsPage), and matches the chart's series name in
 * OverlayChart and its SUMMARY_SERIES_NAMES membership.
 *
 * `displayTitle` is the only thing a user reads. Splitting the two is what lets
 * the Transfer card read "Grid Transfer" without touching a single lookup --
 * and "Transfer" is also the engine's own name for those columns
 * ("Totals Total Transfer (kWh)", "Transfer Import Cost ($)"), where a lookup
 * that misses returns 0 rather than failing, which would put a confident, wrong
 * zero on the card.
 */
export function buildSummaryCards({
  exportedData,
  productionData,
  storageData,
  transferData,
  demandData,
  siteProductionData = productionData,
  siteStorageData = storageData,
} = {}) {
  return [
    {
      /* Keyed "Transfer" everywhere -- visibility, toggles, CSS class, test
       * ids, chart series, engine column names -- and shown as "Grid
       * Transfer". Only this string changes. */
      title: "Transfer",
      flowDirection: cardFlowDirection("Transfer", transferData || {}),
      displayTitle: "Grid Transfer",
      price: transferUtilityBill(transferData, demandData),
      priceIncludesDemand: Boolean(demandData?.hasDemandCharge),
      totalLabel: TRANSFER_TOTAL_LABEL,
      rows: buildTransferFlowRows(transferData || {}, demandData),
      reconciliation: buildSiteReconciliation({
        transferData,
        productionData: siteProductionData,
        storageData: siteStorageData,
        demandData,
      }),
      iconClass: "fa fa-exchange",
      iconBg: "transfer-bg",
      cardClass: "transfer",
      iconColor: "#534fd5",
    },
    {
      title: "Production",
      flowDirection: cardFlowDirection("Production", productionData || {}),
      displayTitle: "Production",
      price: cardNetTotal(
        productionData?.totalCost,
        categoryDemandContribution(demandData, "productionDemand"),
      ),
      priceIncludesDemand: Boolean(demandData?.hasDemandCharge),
      rows: buildProductionFlowRows(productionData || {}, demandData),
      iconClass: "fa fa-sun",
      iconBg: "prod-bg",
      cardClass: "production",
      iconColor: "#ffa500",
    },
    {
      title: "Consumption",
      flowDirection: cardFlowDirection("Consumption", exportedData || {}),
      displayTitle: "Consumption",
      price: cardNetTotal(
        exportedData?.totalCost,
        categoryDemandContribution(demandData, "consumptionDemand"),
      ),
      priceIncludesDemand: Boolean(demandData?.hasDemandCharge),
      rows: buildConsumptionFlowRows(exportedData || {}, demandData),
      iconClass: "fa fa-lightbulb",
      iconBg: "consum-bg",
      cardClass: "consumption",
      iconColor: "#ff0000",
    },
    {
      title: "Storage",
      flowDirection: cardFlowDirection("Storage", storageData || {}),
      displayTitle: "Storage",
      price: cardNetTotal(
        storageData?.value,
        categoryDemandContribution(demandData, "storageDemand"),
      ),
      priceIncludesDemand: Boolean(demandData?.hasDemandCharge),
      rows: buildStorageSummaryFlowRows(storageData || {}, demandData),
      iconClass: "fa fa-battery-half",
      iconBg: "storage-bg",
      cardClass: "storage",
      iconColor: "#1a9f69",
    },
  ];
}

/* ── Which way the power-flow arrow points ─────────────────────────────────
 *
 * From ENERGY, never from the sign of the cost.
 *
 * The diamond used to key its arrows on `cost > 0`. Import versus export is a
 * fact about which way electricity moved and has nothing to do with whether the
 * money came out positive or negative -- and on real data the two disagree.
 * husky-2040-prod imports 12,622,852 kWh and exports 0, so the arrow must point
 * inward; its Transfer Cost is -$925,253, because importing costs money, so
 * `cost > 0` was false and the old rule pointed it outward.
 *
 * Storage had the identical rule. It happens to render correctly on
 * husky-2040-prod -- cost -$461,971 and net charging, so a negative cost and an
 * inward flow coincide -- but that is luck, not design: a battery that
 * discharged more than it charged while still costing money over the period
 * would have pointed the wrong way.
 *
 * Returns "in" (toward the centre of the diamond, or into storage), "out"
 * (away from it), or "none" when nothing moved -- a period with no flow shows
 * no arrow rather than an arbitrary direction. Equal import and export is also
 * "none": the net is zero and neither direction is truer than the other.
 */
const toFlowNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export function cardFlowDirection(title, source = {}) {
  switch (title) {
    case "Transfer": {
      /* Positive net = the site drew from the grid = inward. */
      const net =
        toFlowNumber(source.totalImport) - toFlowNumber(source.totalExport);
      if (net > 0) return "in";
      return net < 0 ? "out" : "none";
    }
    case "Storage": {
      /* Positive net = more went into the batteries than came out = inward. */
      const net =
        toFlowNumber(source.chargeTotal) - toFlowNumber(source.dischargeTotal);
      if (net > 0) return "in";
      return net < 0 ? "out" : "none";
    }
    case "Production":
      /* Generation only ever flows one way: into the system. */
      return toFlowNumber(source.totalProduction) > 0 ? "in" : "none";
    case "Consumption":
      /* Load only ever draws: out of the system, into the building. So the
       * question is whether any energy moved, not which way - the direction is
       * already known.
       *
       * Magnitude, not sign, because the sign is not stable.
       * calculateTotalAreaAndCost returns `(-totalArea).toFixed(2)`, and what
       * that negates differs by path: the backend-column branch sums a column
       * that is already negative, so the export comes out positive, while the
       * fallback branch accumulates absolute values and the export comes out
       * negative. A `> 0` test therefore draws the arm on one path and leaves
       * it dark on the other, for the same site and the same energy.
       *
       * Do not "tidy" this into `> 0` to match its neighbours. Fix the export
       * site first if the sign should be uniform; the tests below cover both
       * shapes so either decision stays honest. */
      return toFlowNumber(source.totalAreaUnderCurve) !== 0 ? "out" : "none";
    default:
      return "none";
  }
}

/* ── How each arm of the diamond animates ─────────────────────────────────
 *
 * A lookup table, not logic: for each card and each direction, the keyframes
 * that move the bar and the angle that puts its solid head at the leading edge.
 *
 * The gradient is written `linear-gradient(<head>deg, transparent, colour)`, so
 * the angle points the way the bar travels and the solid end is the head. That
 * convention is taken from Production and Consumption, which are one-directional,
 * have always been hardcoded, and have never been reported wrong.
 *
 * Transfer's two gradients used to be inverted against it -- the downward
 * keyframe paired with a solid-at-top gradient and the upward one with
 * solid-at-bottom -- so whichever branch ran, the comet's head trailed its own
 * motion and the arrow read backwards. Keeping the pair in one table is what
 * stops them drifting apart again; PowerFlowArrows.test.jsx asserts every pair.
 *
 * Geometry, from the keyframes in Cards.css:
 *   moveVerticalFirstHalf           top  25% -> 100%   down,  centre -> grid
 *   moveVerticalFirstHalfReverse    top 100% ->  45%   up,    grid -> centre
 *   moveVerticalSecondHalf          top -30% ->  25%   down,  production -> centre
 *   moveHorizontalFirstHalf         left  30% -> 100%  right, centre -> load
 *   moveHorizontalSecondHalf        left -40% ->  25%  right, storage -> centre
 *   moveHorizontalSecondHalfReverse left  40% -> -30%  left,  centre -> storage
 */
export const FLOW_HEAD_ANGLE = { up: 0, right: 90, down: 180, left: 270 };

export const POWER_FLOW_ARMS = {
  Transfer: {
    colour: "#675aff",
    /* Inward means energy arriving from the grid, so it travels up the arm
     * toward the centre. */
    in: { keyframes: "moveVerticalFirstHalfReverse", travel: "up" },
    out: { keyframes: "moveVerticalFirstHalf", travel: "down" },
  },
  Storage: {
    colour: "#1a9f69",
    /* Inward means charging: energy leaving the centre for the batteries, which
     * sit at the left end of the horizontal arm. */
    in: { keyframes: "moveHorizontalSecondHalfReverse", travel: "left" },
    out: { keyframes: "moveHorizontalSecondHalf", travel: "right" },
  },
  Production: {
    colour: "#ff9d41",
    in: { keyframes: "moveVerticalSecondHalf", travel: "down" },
  },
  Consumption: {
    colour: "#ff1e1e",
    out: { keyframes: "moveHorizontalFirstHalf", travel: "right" },
  },
};

/** The gradient for one arm leg, head at the leading edge. */
export function flowGradient(colour, travel) {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(colour.slice(i, i + 2), 16));
  return `linear-gradient(${FLOW_HEAD_ANGLE[travel]}deg, rgba(${r}, ${g}, ${b}, 0) 0%, ${colour} 100%)`;
}

/** True when any row on the card is a memo line, so the card knows to show the
 *  footnote that says the money column is not summable. */
export function hasMemoRow(rows = []) {
  return rows.some((row) => row?.variant === "memo");
}


